import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { useNavigate } from 'react-router-dom'

import CompanySelect from '../../ui/CompanySelect'
import { CreateMeasurementDeviceApiModel, useMeasurementDevicesCreateMutation } from '../../common/redux/api/web-api'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import ProductSelect from '../../ui/ProductSelect'
import prevent from '../../lib/prevent'
import Text from '../../ui/Text'
import { Alert } from '../../lib/alert'
import { CheckboxInput } from '../../lib/checkbox-input'
import useText from '../../common/hooks/useText'

const MeasurementDeviceCreateModal: React.FC<Props> = ({ close, dismiss, className, style, errorText }) => {
    const text = useText()
    const navigate = useNavigate()
    const [device, setDevice] = useState<Partial<CreateMeasurementDeviceApiModel>>(() => ({
        ignoreCurrentDirection: true,
        companyId: null,
    }))

    const patchDevice = (params: Partial<CreateMeasurementDeviceApiModel>) =>
        setDevice((device) => ({ ...device, ...params }))
    const [isDeviceErrorShown, setDeviceErrorVisibility] = useState(false)

    const [createNewDevice] = useMeasurementDevicesCreateMutation()

    const createDevice = async () => {
        try {
            await createNewDevice({
                createMeasurementDeviceApiModel: device,
            })
                .unwrap()
                .then((payload) => navigate(`/measurementDevice/${payload.id}`))
        } catch (e) {
            errorText()
        }
    }

    const handleSubmit = () => {
        if (device.type) createDevice(), close(device)
        else setDeviceErrorVisibility(true)
    }

    return (
        <BootstrapModalLayout className={cn(className)} style={style} size="lg">
            <form onSubmit={prevent(handleSubmit)}>
                <div className="modal-header" data-testid="header-md">
                    <Text code="lblCreateMeasurementDevice" />
                </div>
                <div className="modal-body">
                    {isDeviceErrorShown && (
                        <Alert
                            style={{ margin: '15px' }}
                            error={true}
                            message={text('msgChooseProductForMeasurementDevice')}
                        />
                    )}
                    <div className="form-group">
                        <label data-testid="lbl-name">{text('lblMeasurementDeviceName')}</label>
                        <input
                            data-testid="name-input"
                            type="text"
                            className="form-control"
                            value={device?.name ? device.name : ''}
                            onChange={(e) => patchDevice({ name: e?.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label data-testid="lbl-desc">{text('lblMeasurementDeviceDescription')}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={device.description}
                            onChange={(e) => patchDevice({ description: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label data-testid="lbl-detailed-desc">{text('lblMeasurementDeviceDetailedDescription')}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={device.detailedDescription}
                            onChange={(e) => patchDevice({ detailedDescription: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label data-testid="lbl-point">{text('lblMeasurementDevicePointOfInstallation')}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={device.pointOfInstallation}
                            onChange={(e) => patchDevice({ pointOfInstallation: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label data-testid="lbl-product">{text('lblMeasurementDeviceProduct')}</label>
                        <div data-testid="select-product">
                            <ProductSelect
                                data-testid="select"
                                onChange={(type) => {
                                    patchDevice({ type })
                                    setDeviceErrorVisibility(false)
                                }}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label data-testid="lbl-org">{text('lblMeasurementDeviceCompany')}</label>
                        <CompanySelect onChange={(companyId) => patchDevice({ companyId })} />
                    </div>
                    <div className="form-group">
                        <CheckboxInput
                            label={text('lblMeasurementDeviceIgnoreCurrentDirection')}
                            checked={device.ignoreCurrentDirection}
                            setChecked={(checked) => patchDevice({ ignoreCurrentDirection: checked })}
                            dataTestId="ignore-dir"
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-ok" type="submit" data-testid="btn-ok" disabled={!device?.name}>
                        {text('btnOK')}
                    </button>
                    <button className="btn btn-ok" type="button" onClick={() => dismiss()} data-testid="btn-cancel">
                        {text('btnCancel')}
                    </button>
                </div>
            </form>
        </BootstrapModalLayout>
    )
}

export default MeasurementDeviceCreateModal

export type Props = {
    errorText: () => void
    close: (result: CreateMeasurementDeviceApiModel) => void
    dismiss: (reason?: string) => void
    className?: ClassName
    style?: React.CSSProperties
}
